import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { FormField } from "pages/GenericOrder/ui-form/form-field"
import { ModalForm } from "pages/GenericOrder/ui-form/modal-form"
import { useCallback, useEffect, useState, useMemo } from "react"
import { z } from "zod"
import { toast } from "sonner"
import { Img } from "components"

// Enhanced schema with better validation messages
const schema = z.object({
    pincode: z
        .string()
        .regex(/^\d{6}$/, "PIN code must be exactly 6 digits")
        .length(6, "PIN code must be 6 digits"),
    address_line_1: z
        .string()
        .min(1, "Address line 1 is required")
        .max(100, "Address line 1 must be less than 100 characters"),
    address_line_2: z
        .string()
        .max(100, "Address line 2 must be less than 100 characters")
        .optional(),
    property_type: z.enum(["owned", "rented"], { 
        required_error: "Please select property type" 
    }),
    electricity_bill: z
        .instanceof(File, { message: 'Please select a valid file' })
        .refine(file => file.size <= 5 * 1024 * 1024, {
            message: 'File size must be less than 5MB',
        })
        .refine(
            file => ['application/pdf', 'image/jpeg', 'image/png'].includes(file.type),
            {
                message: 'Only PDF, JPEG, and PNG files are allowed',
            }
        ),
    has_premises_images: z.enum(["yes", "no"], { 
        required_error: "Please select an option" 
    }),
    office_front_image: z.instanceof(File).optional(),
    office_interior_image_1: z.instanceof(File).optional(),
    // office_interior_image_2: z.instanceof(File).optional(),
}).refine((data) => {
    if (data.has_premises_images === "yes") {
        return !!(data.office_front_image && data.office_interior_image_1);
    }
    return true;
}, {
    message: "All three premise images are required when 'Yes' is selected",
    path: ["office_front_image"]
});

export type FieldValues = z.infer<typeof schema>;

interface LocationData {
    city: string;
    state: string;
    country: string;
}

interface AddAddressProps {
    children: React.ReactNode;
    onSubmit: (data: FieldValues) => void;
    isLoading?: boolean;
}

const fetchLocationByPincode = async (pincode: string): Promise<LocationData> => {
    console.log(`Fetching location for PIN: ${pincode}`);

    try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Check if the API returned a success status and has PostOffice data
        if (data && data.length > 0 && data[0].Status === "Success" && data[0].PostOffice && data[0].PostOffice.length > 0) {
            const firstPostOffice = data[0].PostOffice[0];
            const location: LocationData = {
                city: firstPostOffice.District, // Using District as city
                state: firstPostOffice.State,
                country: firstPostOffice.Country
            };
            return location;
        } else {
            // If no data or not successful, return a default "Unknown" location
            return {
                city: "Unknown City",
                state: "Unknown State",
                country: "India"
            };
        }
    } catch (error) {
        console.error("Error fetching location:", error);
        // In case of an error (e.g., network issue, API down), reject the promise
        throw new Error('Could not fetch location data. Please try again later.');
    }
};


// Custom hook for location fetching
const useLocationFetch = (pincode: string) => {
    const [locationData, setLocationData] = useState<LocationData | null>(null);
    const [isLocationLoading, setIsLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);

    useEffect(() => {
        const isValidPincode = pincode?.length === 6 && /^\d{6}$/.test(pincode);

        if (!isValidPincode) {
            setLocationData(null);
            setLocationError(null);
            return;
        }

        let isCancelled = false;

        const fetchLocation = async () => {
            setIsLocationLoading(true);
            setLocationError(null);
            setLocationData(null);

            try {
                const location = await fetchLocationByPincode(pincode);
                if (!isCancelled) {
                    setLocationData(location);
                }
            } catch (error) {
                if (!isCancelled) {
                    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch location data';
                    setLocationError(errorMessage);
                    toast.error(errorMessage);
                }
            } finally {
                if (!isCancelled) {
                    setIsLocationLoading(false);
                }
            }
        };

        fetchLocation();

        // Cleanup function to prevent state updates if component unmounts
        return () => {
            isCancelled = true;
        };
    }, [pincode]);

    return { locationData, isLocationLoading, locationError };
};

export default function AddAddress({ children, onSubmit, isLoading = false }: AddAddressProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    // Memoized default values to prevent unnecessary re-renders
    const defaultValues = useMemo(() => ({
        pincode: "",
        address_line_1: "",
        address_line_2: "",
        property_type: "owned" as const,
        has_premises_images: "no" as const,
    }), []);

    const handleFormSubmit = useCallback((data: FieldValues) => {
        console.log("Form Submitted", data);
        onSubmit(data);
        setIsOpen(false);
    }, [onSubmit]);

    const handleFormClose = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleOpenModal = useCallback(() => {
        setIsOpen(true);
    }, []);

    return (
        <>
            <Button variant='default' onClick={handleOpenModal}>
                {children}
            </Button>

            <ModalForm<FieldValues>
                title="Add new address"
                isOpen={isOpen}
                onClose={handleFormClose}
                onSubmit={handleFormSubmit}
                submitButtonText="Save"
                schema={schema}
                defaultValues={defaultValues}
                isLoading={isLoading}
                skipDirtyCheck={true}
                className="sm:max-w-[600px]"
                contentClassName="space-y-6"
            >
                {(form) => <AddressFormContent form={form} />}
            </ModalForm>
        </>
    );
}

// Separate component for form content to improve readability and maintainability
function AddressFormContent({ form }: { form: any }) {
    const watchedPincode = form.watch("pincode");
    const watchedHasPremises = form.watch("has_premises_images");
    
    const { locationData, isLocationLoading, locationError } = useLocationFetch(watchedPincode);

    return (
        <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-3">
            {/* PIN Code Section */}
            <PincodeField 
                form={form}
                locationData={locationData}
                isLocationLoading={isLocationLoading}
                locationError={locationError}
            />

            {/* Address Lines */}
            <AddressFields form={form} />

            {/* Property Type */}
            <PropertyTypeField form={form} />

            {/* Electricity Bill Upload */}
            <ElectricityBillField form={form} />

            {/* Premises Images Question */}
            <PremisesImagesField form={form} />

            {/* Conditional Image Uploads */}
            {watchedHasPremises === "yes" && <PremiseImageUploads form={form} />}
        </div>
    );
}

// Component for PIN code field with location display
function PincodeField({ form, locationData, isLocationLoading, locationError }: {
    form: any;
    locationData: LocationData | null;
    isLocationLoading: boolean;
    locationError: string | null;
}) {
    return (
        <div>
            <label className="block text-gray-700 font-medium mb-2">
                PIN Code<span className="text-red-500">*</span>
            </label>
            <FormField
                form={form}
                name="pincode"
                render={({ field }) => (
                    <>
                        <Input
                            {...field}
                            maxLength={6}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                            placeholder="Enter 6-digit PIN code"
                            inputMode="numeric"
                            pattern="[0-9]*"
                        />
                        <div className="h-4 mt-1">
                            {isLocationLoading && (
                                <span className="text-sm text-gray-500">Fetching location...</span>
                            )}
                            {locationData && !isLocationLoading && (
                                <span className="text-sm text-green-600">
                                    {locationData.city}, {locationData.state}
                                </span>
                            )}
                            {locationError && (
                                <span className="text-sm text-red-500">
                                    Failed to fetch location
                                </span>
                            )}
                        </div>
                    </>
                )}
            />
        </div>
    );
}

// Component for address fields
function AddressFields({ form }: { form: any }) {
    return (
        <>
            <FormField
                form={form}
                name="address_line_1"
                label="Address line 1"
                render={({ field }) => (
                    <Input
                        {...field}
                        placeholder="House No., Street, Society"
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        maxLength={100}
                    />
                )}
            />

            <FormField
                form={form}
                name="address_line_2"
                label="Address line 2 (Optional)"
                render={({ field }) => (
                    <Input
                        {...field}
                        placeholder="Apartment, Landmark, Area"
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        maxLength={100}
                    />
                )}
            />
        </>
    );
}

// Component for property type selection
function PropertyTypeField({ form }: { form: any }) {
    const propertyOptions = [
        { value: "owned", label: "Owned Property" },
        { value: "rented", label: "Rented/Leased" }
    ];

    return (
        <div>
            <label className="block text-gray-700 font-medium mb-3">
                Select the Property Type<span className="text-red-500">*</span>
            </label>
            <FormField
                form={form}
                name="property_type"
                render={({ field }) => (
                    <div className="flex gap-6">
                        {propertyOptions.map(({ value, label }) => (
                            <RadioOption
                                key={value}
                                value={value}
                                label={label}
                                selectedValue={field.value}
                                onChange={field.onChange}
                            />
                        ))}
                    </div>
                )}
            />
        </div>
    );
}

// Component for premises images question
function PremisesImagesField({ form }: { form: any }) {
    const premisesOptions = [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" }
    ];

    return (
        <div>
            <label className="block text-gray-700 font-medium mb-3">
                Do you have premises Images?<span className="text-red-500">*</span>
            </label>
            <FormField
                form={form}
                name="has_premises_images"
                render={({ field }) => (
                    <div className="flex gap-6">
                        {premisesOptions.map(({ value, label }) => (
                            <RadioOption
                                key={value}
                                value={value}
                                label={label}
                                selectedValue={field.value}
                                onChange={field.onChange}
                            />
                        ))}
                    </div>
                )}
            />
        </div>
    );
}

// Reusable radio option component
function RadioOption({ value, label, selectedValue, onChange }: {
    value: string;
    label: string;
    selectedValue: string;
    onChange: (value: string) => void;
}) {
    const isSelected = selectedValue === value;
    
    return (
        <label className="flex items-center gap-2 cursor-pointer">
            <input
                type="radio"
                value={value}
                checked={isSelected}
                onChange={() => onChange(value)}
                className="sr-only"
            />
            <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
            }`}>
                {isSelected && <div className="w-2 h-2 bg-white-A700 rounded-full" />}
            </div>
            <span className={`font-medium ${
                isSelected ? 'text-blue-600' : 'text-gray-600'
            }`}>
                {label}
            </span>
        </label>
    );
}

// Component for electricity bill upload
function ElectricityBillField({ form }: { form: any }) {
    return (
        <div>
            <label className="block text-gray-700 font-medium mb-3">
                Upload Latest Copy of Electricity Bill<span className="text-red-500">*</span>
            </label>
            <FormField
                form={form}
                name="electricity_bill"
                render={({ field }) => (
                    <FileUploader 
                        file={field.value} 
                        onFileChange={field.onChange} 
                        id="electricity-bill" 
                    />
                )}
            />
        </div>
    );
}

// Component for premise image uploads
function PremiseImageUploads({ form }: { form: any }) {
    const imageFields = [
        { name: "office_front_image", label: "Upload Image of Office Front", id: "office-front" },
        { name: "office_interior_image_1", label: "Upload Image of Office Interior", id: "office-interior-1" },
        // { name: "office_interior_image_2", label: "Upload Image of Office Interior", id: "office-interior-2" }
    ];

    return (
        <div className="space-y-4 border-t pt-4">
            <h3 className="font-semibold text-gray-800">
                Premise Images<span className="text-red-500">*</span>
            </h3>
            {imageFields.map(({ name, label, id }) => (
                <FormField
                    key={name}
                    form={form}
                    name={name}
                    render={({ field }) => (
                        <FileUploadField 
                            file={field.value} 
                            onFileChange={field.onChange} 
                            label={label} 
                            id={id} 
                        />
                    )}
                />
            ))}
        </div>
    );
}

// Enhanced primary file uploader component
const FileUploader = ({ file, onFileChange, id }: { 
    file?: File; 
    onFileChange: (file: File | null) => void; 
    id: string; 
}) => {
    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        onFileChange(selectedFile || null);
        e.target.value = ''; // Allow re-uploading the same file
    }, [onFileChange]);

    return (
        <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 overflow-hidden">
                    <Img 
                        src="/images/remit/file.svg" 
                        alt="Upload" 
                        className="w-5 h-5 flex-shrink-0" 
                    />
                    <span className="text-gray-500 truncate">
                        {file ? file.name : "Upload your document"}
                    </span>
                </div>
                <label 
                    htmlFor={id} 
                    className="cursor-pointer bg-blue-600 text-white-A700 px-4 py-2 rounded text-sm hover:bg-blue-700 transition-colors flex-shrink-0"
                >
                    Upload
                </label>
            </div>
            <Input
                type="file" 
                id={id} 
                className="hidden" 
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
            />
        </div>
    );
};

// Enhanced secondary file uploader component
const FileUploadField = ({ file, onFileChange, label, id }: { 
    file?: File; 
    onFileChange: (file: File | null) => void; 
    label: string; 
    id: string; 
}) => {
    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFile = e.target.files?.[0];
        onFileChange(selectedFile || null);
        e.target.value = ''; // Allow re-uploading the same file
    }, [onFileChange]);

    return (
        <div className="flex items-center justify-between py-2">
            <span className="text-gray-700 font-medium">{label}</span>
            <div className="flex items-center gap-2">
                {file && (
                    <span 
                        className="text-sm text-gray-600 max-w-[150px] truncate" 
                        title={file.name}
                    >
                        {file.name}
                    </span>
                )}
                <label 
                    htmlFor={id} 
                    className="cursor-pointer text-blue-700 border border-blue-700 px-4 py-1 rounded text-sm hover:bg-blue-50 transition-colors flex-shrink-0"
                >
                    Upload ↑
                </label>
                <Input
                    type="file" 
                    id={id} 
                    className="hidden" 
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                />
            </div>
        </div>
    );
};